import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { normalizeEmail } from '../../shared/utils/normalize-email';

/**
 * A dedicated API client for fetching and updating user permissions.
 *
 * This service is stateless and is only responsible for making HTTP requests.
 * It abstracts away the details of the API endpoint and the request body,
 * making it a clean, reusable layer for all permissions-related network calls.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionsClientService {
  private http = inject(HttpClient);

  /**
   * Fetches a user's permissions from the backend by their email address.
   *
   * Calls hc-users `GET /api/{org}/user-email?email=<email>`. The org is baked
   * into URL_USERS_NT_GET_USER_BY_EMAIL by the env file.
   *
   * Auth: on cbhi this route is behind the Cognito authorizer and requires the
   * ID token (attached by auth.interceptor for URL_USERS_BASE). It is whitelisted
   * from the not-activated 403 gate so a pending user can still read their own
   * state.
   *
   * Response shape: the cbhi backend returns `role`/`activated` at the ROOT of the
   * record. PermissionsStoreService.extractUserRecord() probes root / userData /
   * userData.userData defensively, so it tolerates either shape.
   */
  async getUserPermissions(email: string): Promise<any> {
    const params = new HttpParams().set('email', email.toLowerCase().trim());
    try {
      return await lastValueFrom(
        this.http.get(environment.URL_USERS_NT_GET_USER_BY_EMAIL, { params })
      );
    } catch (error) {
      console.error('permissions.client.ts | getUserPermissions | Failed to fetch permissions: ', error);
      throw error;
    }
  }

  /**
   * Updates a user's permissions on the backend.
   * @param userId The ID of the user to update.
   * @param permissions The permissions object to apply.
   * @returns A Promise that resolves when the update is complete.
   */
  async updateUserPermissions(userId: string, permissions: object): Promise<void> {
    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    const jsonReadyBody = {
      userId,
      permissions
    };

    try {
      await lastValueFrom(this.http.put(environment.URL_USERS_NT_UPDATE_PERMISSIONS, jsonReadyBody, options));
      console.log('permissions.client.ts | updateUserPermissions | Update successful.');
    } catch (error) {
      console.error('permissions.client.ts | updateUserPermissions | Update failed: ', error);
      throw error;
    }
  }

  /**
   * Flips ONLY a user's `activated` flag (admin-tier action). Distinct from
   * updateUserPermissions so a coarse activate/deactivate never risks overwriting
   * role/sites/legalEntities.
   *
   * Calls hc-users `POST /api/{org}/users/set-activation` body `{ email, activated }`.
   * The URL key is read defensively (only orgs that have wired it define
   * URL_USERS_NT_SET_ACTIVATION); throws a clear error if it's absent so the UI can
   * surface "not available for this org" rather than firing at undefined.
   */
  async setActivation(email: string, activated: boolean): Promise<void> {
    const url = (environment as any).URL_USERS_NT_SET_ACTIVATION as string | undefined;
    if (!url) {
      throw new Error('set-activation endpoint is not configured for this org');
    }
    const body = { email: email.toLowerCase().trim(), activated };
    try {
      await lastValueFrom(this.http.post(url, body, {
        headers: { 'Content-Type': 'application/json' }
      }));
    } catch (error) {
      console.error('permissions.client.ts | setActivation | Update failed: ', error);
      throw error;
    }
  }

  /**
   * Replaces ONLY a user's `emailOptOuts` list (admin-tier action) — the
   * automatic-email categories they should not receive (see EMAIL_CATEGORIES).
   * Same narrow-endpoint reasoning as setActivation.
   *
   * Calls hc-users `POST /api/{org}/users/set-email-opt-outs` body
   * `{ email, emailOptOuts }`. Orgs opt in via URL_USERS_NT_SET_EMAIL_OPT_OUTS;
   * Manage Users hides its Emails column when the key is absent.
   */
  async setEmailOptOuts(email: string, emailOptOuts: string[]): Promise<void> {
    const url = (environment as any).URL_USERS_NT_SET_EMAIL_OPT_OUTS as string | undefined;
    if (!url) {
      throw new Error('set-email-opt-outs endpoint is not configured for this org');
    }
    const body = { email: normalizeEmail(email), emailOptOuts };
    try {
      await lastValueFrom(this.http.post(url, body, {
        headers: { 'Content-Type': 'application/json' }
      }));
    } catch (error) {
      console.error('permissions.client.ts | setEmailOptOuts | Update failed: ', error);
      throw error;
    }
  }

  /**
   * Admin-only: pre-create a {org}-Users record for a person who may not have
   * logged in yet (email + name + initial activation). The backend creates the
   * record with a default least-privilege role (Viewer); the admin assigns the
   * real role/permissions afterward on the Permissions screen.
   *
   * The backend returns 409 if a record for that email already exists — the
   * caller surfaces that as "user already exists" rather than clobbering it.
   * URL key is read defensively (only orgs that wire it define
   * URL_USERS_NT_CREATE_USER); throws a clear error if absent so the UI can say
   * "not available for this org" instead of POSTing to undefined.
   */
  async createUser(payload: { email: string; firstName: string; lastName: string; activated: boolean }): Promise<void> {
    const url = (environment as any).URL_USERS_NT_CREATE_USER as string | undefined;
    if (!url) {
      throw new Error('create-user endpoint is not configured for this org');
    }
    const body = {
      email: normalizeEmail(payload.email),
      firstName: (payload.firstName ?? '').trim(),
      lastName: (payload.lastName ?? '').trim(),
      activated: payload.activated === true,
    };
    try {
      await lastValueFrom(this.http.post(url, body, {
        headers: { 'Content-Type': 'application/json' }
      }));
    } catch (error) {
      console.error('permissions.client.ts | createUser | Create failed: ', error);
      throw error;
    }
  }

  /**
   * Admin-only: remove a user. This is a SOFT delete on the backend — the
   * {org}-Users record is marked `deleted` (retained for audit), and the roster /
   * lookup reads filter it out so it disappears from the UI. The yin to createUser.
   *
   * URL key is read defensively (only orgs that wire it define
   * URL_USERS_NT_DELETE_USER); throws a clear error if absent so the UI can say
   * "not available for this org" instead of POSTing to undefined.
   */
  async deleteUser(email: string): Promise<void> {
    const url = (environment as any).URL_USERS_NT_DELETE_USER as string | undefined;
    if (!url) {
      throw new Error('delete-user endpoint is not configured for this org');
    }
    const body = { email: normalizeEmail(email) };
    try {
      await lastValueFrom(this.http.post(url, body, {
        headers: { 'Content-Type': 'application/json' }
      }));
    } catch (error) {
      console.error('permissions.client.ts | deleteUser | Delete failed: ', error);
      throw error;
    }
  }
}